import { produce } from 'immer';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import {
  EMPLOYEE_LOGOUT_PARAM,
  SESSION_EXPIRED_PARAM,
  isEmployeePublicPath,
  isEoe,
  paths,
} from '../../common/constants/pathVariables.js';
import { fetchExternalAuthMethods } from '../../common/fetch.js';
import { useChangesPoller } from '../../common/hooks/useChangesPoller.js';
import { isStrictFeatureEnabled } from '../../common/utils/featureFlagUtils.js';
import { isInBrowser } from '../../common/utils/ssrUtils';
import { AuxiliaryEsimOrderPhaseEnum } from '../../components/AuxiliaryEsimOrderScene/auxiliaryEsimOrderEnums';
import { setAuxiliaryEsimOrderPhase } from '../../selfservice/actions';
import {
  clearMultisimSubscriptionToken,
  getAuthState,
  getMultisimSubscriptionToken,
  setPrimaryMdmId,
} from '../../selfservice/common/localStorageUtils.js';
import { initiateElisaIdV2LoginWithNoPrompt, removeElisaIdQueryParams } from '../common/util/elisaIdLogin.js';
import { useFeatureFlags } from './ConfigProvider.js';
import { ELISA_ID_IFRAME_ID, ElisaIdSessionChecker } from './ElisaIdSessionChecker';
import { defaultValues as emptyAuthService, useAuthService } from './authService';
import { isCustomer, isCustomerAdmin, isElisaIdV3Enabled, isSameOrigin } from './authUtils';
import type { ElisaIdSessionCheckResultMessage } from './ElisaIdSessionChecker';
import type { AuthServiceProps } from './authService';
import type { AnonymousUser, AuthenticatedUser, User } from './authState';
import type { CommonError } from '../../common/types/errors.js';
import type { AssetCapabilityAction } from '../../generated/api/assetCapabilityAction.js';
import type { AuthenticationMethod } from '../../generated/api/authenticationMethod.js';
import type { Capabilities } from '../../generated/api/capabilities.js';
import type { RolesAndPermissions } from '../../generated/api/rolesAndPermissions.js';
import type { ReactNode } from 'react';

export interface AuthProps extends AuthServiceProps {
  anonymousUser?: AnonymousUser | null | undefined;
  authenticatedUser?: AuthenticatedUser | undefined;
  externalAuthMethods: AuthenticationMethod[] | 'failed' | 'loading';
  isAuthenticated: boolean;
  isLoading: boolean;
  resetLoginForm: boolean;
  ssoSessionValid: boolean;
  elisaIdV2LoginProcessing?: boolean | undefined;
  preElisaIdV2AuthLoading: boolean;
  elisaIdV3Enabled: boolean;
  inMigrationPhase: boolean;
  rolesAndPermissions?: RolesAndPermissions;
  hasPermission: (assetType: keyof Capabilities, action: AssetCapabilityAction, assetId?: string) => boolean;
  externalAuthErrors: CommonError[];
  setExternalAuthErrors: (errors: CommonError[]) => void;
}

export const defaultValues: AuthProps = {
  ...emptyAuthService,
  anonymousUser: undefined,
  externalAuthMethods: [],
  isAuthenticated: false,
  isLoading: false,
  resetLoginForm: false,
  ssoSessionValid: false,
  elisaIdV2LoginProcessing: undefined,
  preElisaIdV2AuthLoading: true,
  elisaIdV3Enabled: false,
  inMigrationPhase: false,
  externalAuthErrors: [],
  setExternalAuthErrors: () => {},
  rolesAndPermissions: undefined,
  hasPermission: () => true,
};

// NOTE!
// - DO NOT use the `AuthContext` outside of this file, except for tests. It is only exported for tests.
// - We have to use default value because `AuthProvider` is placed very high in the React tree and during errors we render
//   `<RootErrorBoundary>` which uses `AuthProvider`. When that happens the `AuthProvider` is not rendered, thus a default
//   context needs to exist so that  `useAuth()` still works. So the above default values should be reasonable ones.
export const AuthContext = createContext(defaultValues);

export const AuthProvider = ({ children }: { children?: ReactNode }) => {
  const [user, setUser] = useState<User>({ anonymous: {} });
  const [extAuthMethods, setExtAuthMethods] = useState<AuthenticationMethod[] | 'failed' | 'loading'>('loading');
  const [externalAuthErrors, setExternalAuthErrors] = useState<CommonError[]>([]);
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const authValidateDispatched = useRef(false);
  const elisaIdV3Enabled = isElisaIdV3Enabled();
  const [elisaIdSessionCheckResultReceived, setElisaIdSessionCheckResultReceived] = useState(false);
  const isElisaIdIframe = isInBrowser() && window.self !== window.top && window.frameElement?.id === ELISA_ID_IFRAME_ID;
  const featureFlags = useFeatureFlags();
  const inMigrationPhase = elisaIdV3Enabled && isStrictFeatureEnabled(featureFlags.inMigrationPhase);
  const cocoEnabled = isStrictFeatureEnabled(featureFlags.coco);
  const [processedLogin] = useState(
    () => isInBrowser() && new URLSearchParams(window.location.search).get('processedLogin') === 'true'
  );

  useEffect(() => {
    if (!processedLogin) {
      return;
    }
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('processedLogin');
    const newSearch = searchParams.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
    window.history.replaceState(null, '', newUrl);
  }, [processedLogin]);

  useChangesPoller(user.authenticated);

  useEffect(() => {
    const fn = async () => setExtAuthMethods(await fetchExternalAuthMethods());
    void fn();
  }, []);

  const setUserState = useCallback((mutation: (user: User) => void): void => {
    setUser(current => produce(current, mutation));
  }, []);

  const ssoSessionValid = !!user.anonymous.ssoSessionValid;
  const resetLoginForm = !!user.anonymous.resetLoginForm;
  const { companyName, firstName, userRole } = user.authenticated || {};
  const isAuthenticated = Boolean(companyName && firstName && isCustomerAdmin(userRole));
  const isAuthenticatedCustomer = Boolean(companyName && firstName && isCustomer(userRole));
  const isLoading =
    !!user.authenticated?.isFetching ||
    user.anonymous.ssoSessionValid === undefined ||
    (elisaIdV3Enabled && !isAuthenticatedCustomer && !elisaIdSessionCheckResultReceived);
  const elisaIdV2LoginProcessing = user.anonymous.elisaIdV2LoginProcessing;
  const preElisaIdV2AuthLoading = !(
    user.anonymous?.ssoSessionValid !== undefined || elisaIdV2LoginProcessing === false
  );
  const { processElisaIdLogin, validateAuth, ...authService } = useAuthService(user, setUserState);

  const isBeforeAuthProcessing = elisaIdV2LoginProcessing === undefined && !ssoSessionValid;
  const skipAutoLogin = search === EMPLOYEE_LOGOUT_PARAM || search === SESSION_EXPIRED_PARAM;

  const rolesAndPermissions = cocoEnabled ? user.authenticated?.rolesAndPermissions : undefined;
  const capabilities = rolesAndPermissions?.capabilities ?? null;

  const hasPermission = (assetType: keyof Capabilities, action: AssetCapabilityAction, assetId?: string): boolean => {
    if (!cocoEnabled || !capabilities) {
      return true;
    }
    const assetCaps = capabilities[assetType];
    if (!assetCaps?.actions?.includes(action)) {
      return false;
    }
    if (!assetId) {
      return true;
    }
    return !assetCaps.assets?.length || assetCaps.assets.includes(assetId);
  };

  const handleElisaIdSessionResult = useCallback(
    (sessionValid: boolean) => {
      setElisaIdSessionCheckResultReceived(true);
      if (sessionValid && !isAuthenticatedCustomer) {
        validateAuth();
      }
    },
    [validateAuth, isAuthenticatedCustomer]
  );

  useEffect(() => {
    if (elisaIdV3Enabled) {
      return;
    }
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('code') && searchParams.get('state') === getAuthState() && !elisaIdV2LoginProcessing) {
      if (getMultisimSubscriptionToken()) {
        dispatch(setAuxiliaryEsimOrderPhase(AuxiliaryEsimOrderPhaseEnum.VALIDATE, getMultisimSubscriptionToken()!));
        clearMultisimSubscriptionToken();
      }
      processElisaIdLogin(searchParams.get('code')!);
      removeElisaIdQueryParams(window.location.pathname, navigate);
    } else if (
      isEoe(pathname) &&
      !isEmployeePublicPath(pathname) &&
      isBeforeAuthProcessing &&
      searchParams.get('error') !== 'login_required'
    ) {
      if (!skipAutoLogin) {
        initiateElisaIdV2LoginWithNoPrompt().then(() => validateAuth());
      }
    } else if (!authValidateDispatched.current && !elisaIdV2LoginProcessing) {
      validateAuth();
      authValidateDispatched.current = true;
    }
    if (searchParams.get('error') && pathname !== paths.AUTHENTICATION_EXT_RETURN) {
      removeElisaIdQueryParams(window.location.pathname, navigate);
    }
  }, [
    dispatch,
    isBeforeAuthProcessing,
    navigate,
    pathname,
    elisaIdV2LoginProcessing,
    skipAutoLogin,
    processElisaIdLogin,
    validateAuth,
    elisaIdV3Enabled,
  ]);

  useEffect(() => {
    if (elisaIdV3Enabled && !isElisaIdIframe && !authValidateDispatched.current) {
      validateAuth();
      authValidateDispatched.current = true;
    }
  }, [elisaIdV3Enabled, validateAuth, isElisaIdIframe]);

  useEffect(() => {
    if (isElisaIdIframe && isSameOrigin()) {
      const message: ElisaIdSessionCheckResultMessage = {
        type: 'elisaIdSessionCheckResult',
        authenticated: !window.location.search.includes('auth_error=login_required'),
      };
      window.parent.postMessage(message, window.location.origin);
    }
  }, [isElisaIdIframe]);

  useEffect(() => {
    const primaryAccount = user.authenticated?.contact?.accountContactRelationships?.find(acr => acr.isPrimaryAccount);
    if (primaryAccount) {
      setPrimaryMdmId(primaryAccount.accountMasterId);
    }
  }, [user.authenticated]);

  if (isElisaIdIframe) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{
        ...authService,
        processElisaIdLogin,
        validateAuth,
        anonymousUser: user.anonymous,
        authenticatedUser: user.authenticated,
        isAuthenticated,
        ssoSessionValid,
        resetLoginForm,
        isLoading,
        elisaIdV2LoginProcessing,
        preElisaIdV2AuthLoading,
        externalAuthMethods: extAuthMethods,
        elisaIdV3Enabled,
        inMigrationPhase,
        rolesAndPermissions,
        hasPermission,
        externalAuthErrors,
        setExternalAuthErrors,
      }}
    >
      {children}
      {elisaIdV3Enabled &&
        isInBrowser() &&
        !isAuthenticatedCustomer &&
        user.anonymous.isValidating === false &&
        !processedLogin && <ElisaIdSessionChecker handleElisaIdSessionResult={handleElisaIdSessionResult} />}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthProps => {
  return useContext(AuthContext);
};
